import { useEffect, useState } from "react";
import { Activity, AlertTriangle, ArrowUpRight, ArrowDownRight, Globe, Loader2, ShieldCheck, Zap, Users } from "lucide-react";
import api, { fmtINR, fmtNum } from "@/lib/api";

/**
 * Admin platform-vitals card. Single GET /api/admin/health-snapshot,
 * polled every 60s. Surfaces anomalies (DNS pending, no active links,
 * volume drop) so the operator can act in seconds without drilling in.
 */
export default function PlatformHealthWidget() {
    const [d, setD] = useState(null);
    const [err, setErr] = useState("");

    const load = async () => {
        try { const r = await api.get("/admin/health-snapshot"); setD(r.data); setErr(""); }
        catch (e) { setErr(e?.response?.data?.detail || e.message); }
    };
    useEffect(() => {
        load();
        const id = setInterval(load, 60_000);
        return () => clearInterval(id);
    }, []);

    if (err) return <div className="card p-5 text-rose-600 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{err}</div>;
    if (!d) return <div className="card p-5"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>;

    // Build alerts list — only render when something needs attention
    const alerts = [];
    if (d.links.dns_pending > 0) alerts.push({
        icon: AlertTriangle, color: "amber",
        text: `${d.links.dns_pending} link${d.links.dns_pending === 1 ? "" : "s"} with custom domain awaiting DNS verification`,
    });
    if (d.partners.active < d.partners.total) alerts.push({
        icon: AlertTriangle, color: "rose",
        text: `${d.partners.total - d.partners.active} partner${d.partners.total - d.partners.active === 1 ? "" : "s"} suspended`,
    });
    if (d.volume_delta_pct < -25 && d.yesterday.volume > 1000) alerts.push({
        icon: ArrowDownRight, color: "rose",
        text: `Today's volume is ${Math.abs(d.volume_delta_pct)}% lower than yesterday`,
    });
    if (d.today.fail > d.today.success && d.today.txns > 10) alerts.push({
        icon: AlertTriangle, color: "rose",
        text: `Failures (${d.today.fail}) outpacing successes (${d.today.success}) today`,
    });

    const trendUp = d.volume_delta_pct > 0;
    const trendCls = d.volume_delta_pct > 5 ? "text-emerald-700"
                   : d.volume_delta_pct < -5 ? "text-rose-700" : "text-slate-500";

    return (
        <div data-testid="platform-health" className="card p-5 space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold text-base flex items-center gap-2">
                    <Activity className="w-4 h-4 text-fuchsia-700" /> Platform Health
                </h3>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">Auto-refreshes 60s</span>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Pill label="Partners" value={fmtNum(d.partners.total)}
                      sub={`${d.partners.active} active · ${d.partners.pro_pct}% on Pro`}
                      icon={Users} accent="blue" />
                <Pill label="Active links" value={fmtNum(d.links.active)}
                      sub={`${d.links.with_custom_domain} on custom domain`}
                      icon={Zap} accent="indigo" />
                <Pill label="Domain verified" value={fmtNum(d.links.dns_verified)}
                      sub={d.links.dns_pending ? `${d.links.dns_pending} pending` : "All clean"}
                      icon={d.links.dns_pending ? Globe : ShieldCheck}
                      accent={d.links.dns_pending ? "amber" : "emerald"} />
                <Pill label="Today's volume"
                      value={fmtINR(d.today.volume)}
                      icon={trendUp ? ArrowUpRight : ArrowDownRight}
                      sub={
                          <span className={`inline-flex items-center gap-0.5 ${trendCls}`}>
                              {trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                              {Math.abs(d.volume_delta_pct)}% vs yesterday
                          </span>
                      }
                      accent={trendUp ? "emerald" : "rose"} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-xs">
                <DayBox title="Today" data={d.today} />
                <DayBox title="Yesterday" data={d.yesterday} muted />
            </div>

            {alerts.length > 0 && (
                <div className="space-y-1.5">
                    {alerts.map((a, i) => {
                        const cls = a.color === "amber" ? "bg-amber-50 border-amber-200 text-amber-800"
                                  : a.color === "rose" ? "bg-rose-50 border-rose-200 text-rose-800"
                                  : "bg-slate-50 border-slate-200 text-slate-700";
                        return (
                            <div key={`${a.text}-${i}`} className={`flex items-center gap-2 px-3 py-2 rounded-md border text-xs ${cls}`}>
                                <a.icon className="w-3.5 h-3.5 flex-shrink-0" />
                                <span>{a.text}</span>
                            </div>
                        );
                    })}
                </div>
            )}
            {alerts.length === 0 && (
                <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-md flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    All systems green.
                </div>
            )}
        </div>
    );
}

function Pill({ label, value, sub, icon: Icon, accent }) {
    const map = {
        blue: "bg-blue-50 text-blue-700",
        indigo: "bg-indigo-50 text-indigo-700",
        emerald: "bg-emerald-50 text-emerald-700",
        amber: "bg-amber-50 text-amber-700",
        rose: "bg-rose-50 text-rose-700",
    };
    return (
        <div className="rounded-lg border border-slate-200 p-3 hover:bg-slate-50/40 transition">
            <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</span>
                <span className={`w-6 h-6 rounded flex items-center justify-center ${map[accent]}`}><Icon className="w-3 h-3" /></span>
            </div>
            <div className="text-xl font-bold font-mono tracking-tight">{value}</div>
            <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div>
        </div>
    );
}

function DayBox({ title, data, muted }) {
    return (
        <div className={`rounded-md border ${muted ? "border-slate-100 bg-slate-50/40" : "border-slate-200"} p-3`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">{title}</div>
            <div className="grid grid-cols-4 gap-2 text-center">
                <Mini label="Txns" value={fmtNum(data.txns)} />
                <Mini label="Success" value={fmtNum(data.success)} accent="emerald" />
                <Mini label="Failed" value={fmtNum(data.fail)} accent={data.fail > 0 ? "rose" : null} />
                <Mini label="Conv." value={`${data.conversion}%`}
                      accent={data.conversion > 70 ? "emerald" : data.conversion < 40 ? "rose" : null} />
            </div>
        </div>
    );
}

function Mini({ label, value, accent }) {
    const cls = accent === "emerald" ? "text-emerald-700"
              : accent === "rose" ? "text-rose-700" : "text-slate-900";
    return (
        <div>
            <div className={`font-mono font-bold text-sm ${cls}`}>{value}</div>
            <div className="text-[9px] uppercase tracking-wider text-slate-400">{label}</div>
        </div>
    );
}
